/**
 * PostHog Analytics Wrapper — Issue #168
 *
 * Thin wrapper around posthog-js for user behavior analytics.
 * Cookieless mode (privacy-first), shared project distinguished by app_name.
 *
 * Usage:
 *   import { initPostHog, identifyUser, resetUser, captureEvent } from './posthog';
 */

import posthog from 'posthog-js';
import { createLogger } from '@/shared/utils/logger';

const posthogLog = createLogger('PostHog');

let initialized = false;

/** PostHog env config — reads Vite env vars */
function getConfig() {
  return {
    key: import.meta.env.VITE_POSTHOG_KEY as string | undefined,
    host: import.meta.env.VITE_POSTHOG_HOST as string | undefined,
    appName: (import.meta.env.VITE_POSTHOG_APP_NAME as string) || 'novelforge',
  };
}

/**
 * Initialize PostHog SDK. Safe to call multiple times (idempotent).
 * Returns true if initialization succeeded, false if skipped (missing config).
 */
export function initPostHog(): boolean {
  if (initialized) return true;

  const { key, host, appName } = getConfig();
  if (!key || !host) {
    if (import.meta.env.DEV) {
      posthogLog.debug('Skipped: VITE_POSTHOG_KEY or VITE_POSTHOG_HOST not set');
    }
    return false;
  }

  try {
    posthog.init(key, {
      api_host: host,
      // Privacy-first: no cookies, no persistent device ID
      persistence: 'memory',
      // Auto page view capture — we handle it manually via React Router
      capture_pageview: false,
      // Auto page leave capture for time-on-page metrics
      capture_pageleave: true,
      // Session recording
      enable_recording_console_log: false,
      // Disable autocapture (we use explicit events)
      autocapture: false,
    });

    // Register global property to distinguish app in shared PostHog project
    posthog.register({ app_name: appName });

    initialized = true;
    return true;
  } catch (err) {
    posthogLog.debug('Init error', { error: String(err) });
    return false;
  }
}

/**
 * Identify authenticated user. Call after login.
 */
export function identifyUser(userId: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  try {
    posthog.identify(userId, properties);
  } catch {
    // Analytics should never break the app
  }
}

/**
 * Reset user identity. Call on logout.
 */
export function resetUser(): void {
  if (!initialized) return;
  try {
    posthog.reset();
  } catch {
    // Analytics should never break the app
  }
}

/**
 * Capture a single event to PostHog.
 */
export function captureEvent(eventName: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  try {
    posthog.capture(eventName, properties);
  } catch {
    // Analytics should never break the app
  }
}

/**
 * Capture a page view event.
 */
export function capturePageView(path: string): void {
  if (!initialized) return;
  try {
    posthog.capture('$pageview', { $current_url: window.location.href, path });
  } catch {
    // Analytics should never break the app
  }
}

/**
 * Check if PostHog is initialized (for testing).
 */
export function isPostHogInitialized(): boolean {
  return initialized;
}

/**
 * Reset initialization state (for testing only).
 */
export function _resetForTesting(): void {
  initialized = false;
}
